import React, { memo, useMemo } from 'react'
import tw from 'twin.macro'
import { Tooltip, Typography } from '@mui/material'
import { GSIcon } from 'src/components/shared/GSIcon'
import { collectedDataStepIconsMap, CollectedDataStepType } from 'src/constants/collectedData'
import { customerComplianceStepIcons } from 'src/constants/customer/icons'
import { customerComplianceStepTitles } from 'src/constants/customer/constants'
import { getCollectDataStepTitle } from 'src/utils/collectedData'
import { ContractorStepDetailsCardStatusBadge } from './ContractorStepDetailsCardStatusBadge'
import {
  ContractorStepDetailsCardUsageVariant,
  type ContractorStepDetailsCardStatus,
} from '../constants'
import type { CollectedData } from 'src/interfaces/collectedData'
import type { CustomerCollectedDataTypeExtended } from 'src/constants/customer/enums'
import { colors } from 'src/theme'
import { RecollectionIndicator } from 'src/components/contractor-list/shared/recollection-indicator'
import { ContractorRecollection } from 'src/interfaces/contractor'
import { DateTime } from 'luxon'

export interface ContractorStepDetailsCardHeaderProps {
  usageVariant: ContractorStepDetailsCardUsageVariant
  stepType?: CustomerCollectedDataTypeExtended
  collectedData?: CollectedData | null
  status: ContractorStepDetailsCardStatus
  isRecollecting?: boolean
  recollectionData?: ContractorRecollection[]
  isOptional?: boolean
  paymentMethodUpdatedAt?: number | null
}

const ContractorStepDetailsCardHeaderComponent = ({
  usageVariant,
  stepType,
  collectedData,
  status,
  isRecollecting = false,
  recollectionData,
  isOptional = false,
  paymentMethodUpdatedAt,
}: ContractorStepDetailsCardHeaderProps) => {
  const { title, icon } = useMemo(() => {
    const isMasterContractor =
      usageVariant === ContractorStepDetailsCardUsageVariant.MasterContractor && stepType != null

    if (isMasterContractor) {
      return {
        title: customerComplianceStepTitles[stepType],
        icon: customerComplianceStepIcons[stepType],
      }
    }

    if (collectedData != null) {
      return {
        title: getCollectDataStepTitle(collectedData),
        icon: collectedDataStepIconsMap[collectedData.collectDataStep.type],
      }
    }

    return { title: '', icon: null }
  }, [usageVariant, stepType, collectedData])

  const isPaymentMethodStep =
    collectedData?.collectDataStep.type === CollectedDataStepType.PaymentMethod

  const lastActivityLabel = useMemo(() => {
    if (!isPaymentMethodStep || !paymentMethodUpdatedAt) {
      return null
    }

    return DateTime.fromMillis(paymentMethodUpdatedAt * 1000).toFormat('MMM d, h:mm a')
  }, [isPaymentMethodStep, paymentMethodUpdatedAt])

  return (
    <div css={tw`flex flex-row items-center justify-between flex-wrap gap-2`}>
      <div css={tw`flex flex-row items-center min-w-0 flex-1`}>
        {icon && <div css={tw`flex flex-shrink-0 items-center`}>{icon}</div>}
        <div css={tw`flex flex-col justify-center min-w-0 ml-2`}>
          {lastActivityLabel ? (
            <Tooltip
              title={
                <div css={tw`pb-1`}>
                  <Typography css={tw`mb-1`} variant="h6" color={colors.SHADES_GREY_200}>
                    Last activity
                  </Typography>
                  <Typography variant="body2" color={colors.SHADES_GREY_300}>
                    Settlement method updated at {lastActivityLabel}
                  </Typography>
                </div>
              }
              placement="right-end"
            >
              <span css={tw`cursor-pointer`}>
                <Typography variant="body2" fontWeight={700} css={tw`m-0 underline`}>
                  {title}
                </Typography>
              </span>
            </Tooltip>
          ) : (
            <Typography variant="body2" fontWeight={700} css={tw`m-0`}>
              {title}
            </Typography>
          )}
        </div>
      </div>
      <div css={tw`flex flex-row items-center gap-2 flex-shrink-0`}>
        {isRecollecting && (
          <div css={tw`flex flex-shrink-0 items-center`}>
            {recollectionData?.length ? (
              <RecollectionIndicator recollections={recollectionData} muted />
            ) : (
              <GSIcon iconName="gs_arrows_repeat_right_left" width={20} />
            )}
          </div>
        )}
        {isOptional && (
          <div
            css={[tw`px-2 py-0.5 rounded-[10px] my-2`, { backgroundColor: colors.SHADES_GREY_200 }]}
          >
            <Typography variant="subtitle2" color={colors.SHADES_GREY_600}>
              Optional
            </Typography>
          </div>
        )}
        <ContractorStepDetailsCardStatusBadge status={status} />
      </div>
    </div>
  )
}

export const ContractorStepDetailsCardHeader = memo(ContractorStepDetailsCardHeaderComponent)
